import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import Skeleton from '@/components/ui/Skeleton'
import { useMemberProfile, useMe } from '@/features/account/api/queries'
import { lastUpdatedMeta } from '@/lib/view-state'
import AccountPageHeader from '@/screens/account/components/AccountPageHeader'
import { settingsTitle } from '@/screens/account/components/account-tabs'
import AccountClosing from '@/screens/account/components/settings/AccountClosing'
import AppearancePanel from '@/screens/account/components/settings/AppearancePanel'
import EditProfilePanel from '@/screens/account/components/settings/EditProfilePanel'
import HelpPanel from '@/screens/account/components/settings/HelpPanel'
import NotificationsPanel from '@/screens/account/components/settings/NotificationsPanel'
import PrivacyPanel from '@/screens/account/components/settings/PrivacyPanel'
import SecurityPanel from '@/screens/account/components/settings/SecurityPanel'
import { loadSettings, parseSettingsSearch } from '@/screens/account/SettingsScreen.loader'

/**
 * The two tabs the loader (`SettingsScreen.loader.ts`) actually awaits, before
 * their reads land: `edit-profile` gets the same `h-110` block the screen's own
 * loading branch below draws, `security` its Password group's. Every other tab
 * resolves the loader synchronously, so this is never on screen for them.
 */
function SettingsPending() {
  const { tab } = Route.useSearch()
  return (
    <ScreenLayout header={<AccountPageHeader title={settingsTitle(tab)} />} pending fullWidth>
      <Skeleton className={tab === 'security' ? 'h-24' : 'h-110'} />
    </ScreenLayout>
  )
}

/** The copy the `edit-profile` tab used for a failed `me`/`profile` read before it had a loader. */
function SettingsError(props: ErrorComponentProps) {
  const { tab } = Route.useSearch()
  return (
    <RouteErrorPage
      {...props}
      header={<AccountPageHeader title={settingsTitle(tab)} />}
      title="Your details did not load"
    />
  )
}

/**
 * The six Account settings pages, addressed by `?tab=`.
 *
 * `validateSearch` is what makes the tab a typed part of the URL: every
 * `<Link to="/settings" search={{ tab }} />` in this family is checked against
 * it, and a hand-typed `?tab=securtiy` falls back to the first tab instead of
 * landing the member on a router error boundary. See
 * `components/account-tabs.ts` for why these are one route and not seven, and
 * `SettingsScreen.loader.ts` for why `parseSettingsSearch` (not a bare
 * `parseSettingsTab` call) backs this AND the loader's own read of the URL.
 */
export const Route = createFileRoute('/_app/settings')({
  validateSearch: parseSettingsSearch,
  loader: loadSettings,
  pendingComponent: SettingsPending,
  errorComponent: SettingsError,
  component: SettingsScreen,
})

function SettingsScreen() {
  const { tab } = Route.useSearch()
  const me = useMe()
  const profile = useMemberProfile()
  const header = <AccountPageHeader title={settingsTitle(tab)} />
  // Set once Delete my account succeeds: the page becomes the closing screen in place, with no
  // title row, until the member leaves (the session is already gone on mm-be).
  const [closed, setClosed] = useState(false)

  if (closed) {
    return (
      <ScreenLayout header={null} fullWidth>
        <AccountClosing />
      </ScreenLayout>
    )
  }

  // Only Edit profile is drawn from the member as a whole. The others are
  // settings this browser holds, so they must not wait on a request — a
  // spinner in front of the theme picker would be absurd. (Security's Password
  // row reads `profile` itself, through its own QueryState.)
  //
  // Still a plain query, not the route's suspense pattern: a member who
  // switches TO this tab from another one search-only (no path change) runs
  // no loader — see the loader's own doc — so this hook is what actually
  // fetches then, and it is what draws the branches below while it does. On
  // a fresh navigation that lands directly on this tab, the loader has
  // already `ensureRouteData`d both, so `isLoading` is already false by the
  // time this mounts and neither branch below ever paints.
  if (tab === 'edit-profile') {
    if (me.isLoading || profile.isLoading) {
      return (
        <ScreenLayout header={header} fullWidth>
          <Skeleton className="h-110" />
        </ScreenLayout>
      )
    }
    // Gated on *data*, not `isError` — the same rule `ProfileScreen.tsx`
    // documents for this identical `me`/`profile` pair: both use
    // `staleTime: Infinity`, so a background refetch's failure must not swap
    // a page that already has good cached data for this block.
    if (!me.data || !profile.data) {
      const lastUpdatedAt = Math.max(me.dataUpdatedAt, profile.dataUpdatedAt)
      return (
        <ScreenLayout header={header} fullWidth>
          <EmptyState
            kind="failed"
            size="page"
            title="Your details did not load"
            onRetry={() => {
              void me.refetch()
              void profile.refetch()
            }}
            retrying={me.isFetching || profile.isFetching}
            meta={lastUpdatedMeta(lastUpdatedAt)}
          />
        </ScreenLayout>
      )
    }
    return (
      <ScreenLayout header={header} fullWidth>
        <EditProfilePanel me={me.data} profile={profile.data} />
      </ScreenLayout>
    )
  }

  return (
    <ScreenLayout header={header} fullWidth>
      {tab === 'notifications' && <NotificationsPanel />}
      {tab === 'appearance' && <AppearancePanel />}
      {tab === 'security' && <SecurityPanel />}
      {tab === 'privacy' && (
        <PrivacyPanel
          onClosed={() => {
            setClosed(true)
          }}
        />
      )}
      {tab === 'help' && <HelpPanel />}
    </ScreenLayout>
  )
}
